import { Button, Modal, Typography } from 'antd';

import { formatCount } from '../../utils/counts';
import { refusalAsksClear, refusalAsksLookupClear, refusalAsksOwnUnits, shapeReviewPath, shapeWords } from '../../utils/shapeChange';
import { getMeasure } from '../../utils/units';
import { EXTERNAL_LINK_PROPS } from '../common/ExternalLink';

/**
 * "What happens to the values already there?" — the dialog the card editor
 * (and the element set editor) raises when "Allow multiple" is turned on or
 * off on a SAVED element of a list type — a Choice, a date, a date and time,
 * an item lookup, a user — whose items hold values in the old shape, and when
 * a save meets the API's 409 values_need_conversion (owner ruling B16;
 * utils/shapeChange.js). The switch does not move until the author answers.
 * The words follow the type (shapeWords: "choice", "date", "date and time",
 * "linked item", "user"; a date keeps its EARLIEST, the rest their first):
 *
 *   on   "Make each item's single date a list of one"       → onConvert('wrap')
 *   off  "Review the N items first"                          → opens the list of
 *        the items holding more than one, in a new tab; the switch stays put
 *        "Keep the earliest date on each of the N items"     → onConvert('first'),
 *        red and last: the other dates are dropped when the card is saved
 *   off, no item holding more than one: "Keep each item's one date" — nothing
 *        is lost, so it is the primary, not a danger
 *
 * Cancel (focused, as every destructive confirm focuses it) leaves the switch
 * where it was. Nothing is written here: the answer rides on the element until
 * the card is saved.
 *
 * `refusal` is the API's entry for this element: { direction, from, to, items,
 * multiple, lossy, review }.
 */
export default function MultiToggleDialog({ open, refusal, label, collectionId = null, onConvert, onCancel, asksDetach = false }) {
	const entry = refusal || {};
	if (asksDetach) {
		return <DetachDialog open={open} entry={entry} label={label} onConvert={onConvert} onCancel={onCancel} />;
	}
	if (refusalAsksOwnUnits(entry)) {
		return <ClearOwnUnitsDialog open={open} entry={entry} label={label} collectionId={collectionId} onConvert={onConvert} onCancel={onCancel} />;
	}
	if (refusalAsksLookupClear(entry)) {
		return <LookupClearDialog open={open} entry={entry} label={label} collectionId={collectionId} onConvert={onConvert} onCancel={onCancel} />;
	}
	if (refusalAsksClear(entry)) {
		return <ClearMeasuresDialog open={open} entry={entry} label={label} collectionId={collectionId} onConvert={onConvert} onCancel={onCancel} />;
	}
	const name = label || entry.element || 'this element';
	const words = shapeWords(entry);
	// The switch as the author saw it: the Dates panel's reads "Multiple
	// values" (its own older word), every other list type's "Allow multiple".
	const switchType = entry.direction === 'on' ? entry.to : entry.from;
	const switchLabel = switchType === 'date' || switchType === 'date-time' ? 'Multiple values' : 'Allow multiple';
	const retyped = entry.from && entry.to && entry.from !== entry.to;
	const title = retyped
		? `Change the type of "${name}"?`
		: (entry.direction === 'on' ? `Turn on ${switchLabel}?` : `Turn off ${switchLabel}?`);
	const items = formatCount(entry.items, 'item') || 'Some items';
	const multiple = Number(entry.multiple) || 0;
	const reviewPath = entry.direction === 'off' && multiple ? shapeReviewPath(entry.review, collectionId) : null;
	const theItems = n => (n === 1 ? 'the item' : `the ${n.toLocaleString('en-US')} items`);
	const keeps = `${words.keeps} ${words.one}`;
	// A file list keeps its first file and MOVES the others to each item's
	// Other attachments (PR 10b): nothing is lost, so the answer is not red.
	const filesMove = words.type === 'file';

	let body;
	let choices;
	if (entry.direction === 'on') {
		body = `${items} hold${entry.items === 1 ? 's' : ''} a single value in "${name}". With ${switchLabel} on, every item holds a list.`;
		choices = [
			<Button key='wrap' type='primary' block className='multi-toggle-dialog__choice' onClick={() => onConvert('wrap')}>
				Make each item&apos;s single {words.one} a list of one
			</Button>,
		];
	} else if (multiple) {
		body = `${items} hold${entry.items === 1 ? 's' : ''} a list of ${words.many} in "${name}", and ${multiple === 1 ? '1 of them holds' : `${multiple.toLocaleString('en-US')} of them hold`} more than one. With ${switchLabel} off, every item holds one ${words.one}.`;
		choices = [
			reviewPath ? (
				<Button key='review' block className='multi-toggle-dialog__choice' href={reviewPath} {...EXTERNAL_LINK_PROPS} onClick={onCancel}>
					Review {theItems(multiple)} first
				</Button>
			) : null,
			<Button key='first' danger={!filesMove} block className='multi-toggle-dialog__choice' onClick={() => onConvert('first')}>
				Keep the {keeps} on {multiple === 1 ? 'the item' : `each of the ${multiple.toLocaleString('en-US')} items`}
			</Button>,
		].filter(Boolean);
	} else {
		body = `${items} hold${entry.items === 1 ? 's' : ''} a list in "${name}", each with one ${words.one} at most, so nothing is lost.`;
		choices = [
			<Button key='first' type='primary' block className='multi-toggle-dialog__choice' onClick={() => onConvert('first')}>
				Keep each item&apos;s one {words.one}
			</Button>,
		];
	}

	return (
		<Modal
			open={open}
			title={title}
			onCancel={onCancel}
			destroyOnHidden
			footer={[<Button key='cancel' autoFocus onClick={onCancel}>Cancel</Button>]}
			className='multi-toggle-dialog'
		>
			<Typography.Paragraph>{body}</Typography.Paragraph>
			<div className='multi-toggle-dialog__choices' data-testid='multi-toggle-choices'>{choices}</div>
			{entry.direction === 'off' && multiple ? (
				<Typography.Paragraph type='secondary' className='multi-toggle-dialog__note'>
					{filesMove
						? `Keeping the ${keeps} moves the others to each item's Other attachments when you save the card. Nothing is deleted.`
						: (reviewPath
							? `Reviewing opens ${entry.review?.truncated ? `the first ${Number(entry.review.items).toLocaleString('en-US')} of those items` : 'those items'} in a new tab, and leaves ${switchLabel} on until you change them. Keeping the ${keeps} drops the others when you save the card.`
							: `Keeping the ${keeps} drops the others when you save the card. To change those items by hand first, cancel and edit them.`)}
				</Typography.Paragraph>
			) : null}
		</Modal>
	);
}

/**
 * "Photos only" turned on over a saved file element whose items hold files
 * that are not photos (the 10b contract: 409 values_need_conversion until
 * `convertValues: 'detach'`). The one answer moves those files to each item's
 * Other attachments when the card is saved — nothing is deleted, so it is the
 * primary, not a danger. Cancel leaves the element taking any file.
 */
function DetachDialog({ open, entry, label, onConvert, onCancel }) {
	const name = label || entry.element || 'this element';
	const items = formatCount(entry.items, 'item') || 'Some items';
	const files = Number(entry.files) || 0;
	return (
		<Modal
			open={open}
			title='Turn on Photos only?'
			onCancel={onCancel}
			destroyOnHidden
			footer={[<Button key='cancel' autoFocus onClick={onCancel}>Cancel</Button>]}
			className='multi-toggle-dialog'
		>
			<Typography.Paragraph>
				{`${items} hold${entry.items === 1 ? 's' : ''} ${files ? formatCount(files, 'file') : 'files'} in "${name}" that ${files === 1 ? 'is not a photo' : 'are not photos'}. With Photos only on, every file there must be a photo.`}
			</Typography.Paragraph>
			<div className='multi-toggle-dialog__choices' data-testid='multi-toggle-choices'>
				<Button type='primary' block className='multi-toggle-dialog__choice' onClick={() => onConvert('detach')}>
					Move the files that are not photos to Other attachments
				</Button>
			</div>
			<Typography.Paragraph type='secondary' className='multi-toggle-dialog__note'>
				They move when you save the card. Nothing is deleted.
			</Typography.Paragraph>
		</Modal>
	);
}

/** "smidgen", "smidgen and dollop" — the own units a refusal names. */
function ownUnitWords(units) {
	const words = (units || []).filter(own => own && own.items).map(own => own.singular || own.id);
	if (words.length < 2) return words[0] || 'that unit';
	return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/**
 * An own unit taken away (KPR-1109: the 409 values_need_conversion with
 * `direction: 'ownUnits'`). The API takes two answers, `move` (to another
 * unit, keeping the numbers, through a unit map) and `clear`; this dialog
 * offers `clear` -- red and last, after the door to review the items -- and
 * says that renaming the unit keeps its values. Moving them waits for the
 * Change card utility's mapping step (KPR-707), which draws the unit map; no
 * second mapping editor is built here. Cancel (focused) puts the units back.
 */
function ClearOwnUnitsDialog({ open, entry, label, collectionId, onConvert, onCancel }) {
	const name = label || entry.element || 'this element';
	const removed = ownUnitWords(entry.removed);
	const count = Number(entry.items) || 0;
	const items = formatCount(count, 'item') || 'Some items';
	const reviewPath = shapeReviewPath(entry.review, collectionId);
	const theItems = count === 1 ? 'the item' : `the ${count.toLocaleString('en-US')} items`;
	return (
		<Modal
			open={open}
			title={`Remove ${removed}?`}
			onCancel={onCancel}
			destroyOnHidden
			footer={[<Button key='cancel' autoFocus onClick={onCancel}>Cancel</Button>]}
			className='multi-toggle-dialog'
		>
			<Typography.Paragraph>
				{`${items} hold${count === 1 ? 's' : ''} a value in ${removed} in "${name}". To keep those values under another name, rename the unit instead of removing it.`}
			</Typography.Paragraph>
			<div className='multi-toggle-dialog__choices' data-testid='multi-toggle-choices'>
				{reviewPath ? (
					<Button block className='multi-toggle-dialog__choice' href={reviewPath} {...EXTERNAL_LINK_PROPS} onClick={onCancel}>
						Review {theItems} first
					</Button>
				) : null}
				<Button danger block className='multi-toggle-dialog__choice' onClick={() => onConvert('clear')}>
					Clear the {removed} {count === 1 ? 'value' : 'values'} on {theItems}
				</Button>
			</div>
			<Typography.Paragraph type='secondary' className='multi-toggle-dialog__note'>
				Clearing empties those values when you save the card. The values in every other unit stay.
			</Typography.Paragraph>
		</Modal>
	);
}

/** "mass", "mass and count" — the measures a refusal names, as words. */
function measureWords(ids) {
	const words = (ids || []).map(id => (getMeasure(id)?.label || String(id)).toLowerCase());
	if (words.length < 2) return words[0] || 'that measure';
	return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/**
 * A measure taken away from a measurement element that allows several
 * (KPR-1085: the 409 values_need_conversion with `direction: 'measures'`,
 * answered `clear`). A mass is no volume without knowing what is weighed, so
 * the one answer EMPTIES those values when the card is saved — red and last,
 * after the door to review them; the values in the measures the element keeps
 * stay. Cancel (focused) puts the measures back.
 */
function ClearMeasuresDialog({ open, entry, label, collectionId, onConvert, onCancel }) {
	const name = label || entry.element || 'this element';
	const removed = measureWords(entry.removed);
	const kept = measureWords(entry.measures);
	const count = Number(entry.items) || 0;
	const items = formatCount(count, 'item') || 'Some items';
	const reviewPath = shapeReviewPath(entry.review, collectionId);
	const theItems = count === 1 ? 'the item' : `the ${count.toLocaleString('en-US')} items`;
	return (
		<Modal
			open={open}
			title={`Stop allowing ${removed}?`}
			onCancel={onCancel}
			destroyOnHidden
			footer={[<Button key='cancel' autoFocus onClick={onCancel}>Cancel</Button>]}
			className='multi-toggle-dialog'
		>
			<Typography.Paragraph>
				{`${items} hold${count === 1 ? 's' : ''} a value in ${removed} in "${name}". A value in ${removed} cannot become ${kept}, so it has nowhere to go.`}
			</Typography.Paragraph>
			<div className='multi-toggle-dialog__choices' data-testid='multi-toggle-choices'>
				{reviewPath ? (
					<Button block className='multi-toggle-dialog__choice' href={reviewPath} {...EXTERNAL_LINK_PROPS} onClick={onCancel}>
						Review {theItems} first
					</Button>
				) : null}
				<Button danger block className='multi-toggle-dialog__choice' onClick={() => onConvert('clear')}>
					Clear the {removed} {count === 1 ? 'value' : 'values'} on {theItems}
				</Button>
			</div>
			<Typography.Paragraph type='secondary' className='multi-toggle-dialog__note'>
				{`Clearing empties those values when you save the card. The values in ${kept} stay.`}
			</Typography.Paragraph>
		</Modal>
	);
}

/**
 * An element made an item lookup while items hold values under its name
 * (KPR-1136, the owner's ruling of 2026-10-22: 409 type_change_needs_empty
 * with `answer: 'clear'`) — retyped from a text, say, or a lookup added back
 * over a removed element's values. A lookup holds only links someone chose,
 * and a typed value cannot be told from one, so the one answer EMPTIES those
 * values when the card is saved: red and last, after the door to review them.
 * Cancel (focused) leaves the change in the draft, unsaved.
 */
function LookupClearDialog({ open, entry, label, collectionId, onConvert, onCancel }) {
	const name = label || entry.element || 'this element';
	const count = Number(entry.items ?? entry.count) || 0;
	const items = formatCount(count, 'item') || 'Some items';
	const reviewPath = shapeReviewPath(entry.review, collectionId);
	const theItems = count === 1 ? 'the item' : `the ${count.toLocaleString('en-US')} items`;
	const values = count === 1 ? '1 value' : `${count.toLocaleString('en-US')} values`;
	return (
		<Modal
			open={open}
			title={`Make "${name}" an item lookup?`}
			onCancel={onCancel}
			destroyOnHidden
			footer={[<Button key='cancel' autoFocus onClick={onCancel}>Cancel</Button>]}
			className='multi-toggle-dialog'
		>
			<Typography.Paragraph>
				{`${items} hold${count === 1 ? 's' : ''} a value under "${name}"${entry.hidden ? ', kept from an element of that name removed earlier' : ''}. An item lookup holds only links someone chose, so those values cannot become links.`}
			</Typography.Paragraph>
			<div className='multi-toggle-dialog__choices' data-testid='multi-toggle-choices'>
				{reviewPath ? (
					<Button block className='multi-toggle-dialog__choice' href={reviewPath} {...EXTERNAL_LINK_PROPS} onClick={onCancel}>
						Review {theItems} first
					</Button>
				) : null}
				<Button danger block className='multi-toggle-dialog__choice' onClick={() => onConvert('clear')}>
					Clear {values} and make it a lookup
				</Button>
			</div>
			<Typography.Paragraph type='secondary' className='multi-toggle-dialog__note'>
				Clearing empties those values when you save the card. To keep them, cancel and add the lookup under another name.
			</Typography.Paragraph>
		</Modal>
	);
}
